import getCompanyIdForRequest from '@/lib/getCompanyIdForRequest';
import getPlatformApiBaseUrl from '@/lib/getPlatformApiBaseUrl';
import handleOrgAccessRestricted from '@/lib/handleOrgAccessRestricted';
import dispatchHelper from '@/lib/redux/dispatchHelper';
import nonBlockingWaitToBeLoaded from '@/lib/redux/nonBlockingWaitToBeLoaded';
import sendToast from '@/lib/sendToast';
import { store } from '@/redux/lib/configureStore';
import xMindcloudInstance from '@/schema/xMindcloudInstance';
import { mapObjectToQueryParams } from '@shared/utils/mapObjectToQueryParams.js';
import { parseJson } from '@shared/utils/parseJson.js';
import axios from 'axios';
import { ORG_ACCESS_RESTRICTED_CODE } from 'shared/schema/staffAccess.js';

const BASE_API = getPlatformApiBaseUrl();

const handleAPIRequest = async (config) => {
  const actionTypes = Array.isArray(config.action) ? config.action : [config.action];
  const serverCallback = config.serverCallback;
  const cancelRef = config.cancelRef;
  let callback = config.callback;

  // check if there is an ongoing request to be cancelled
  if (cancelRef?.current && typeof cancelRef?.current?.cancel === 'function') {
    cancelRef?.current?.cancel('Operation canceled due to new request.');
    // cleanup ref
    cancelRef.current = undefined;
  }

  // generate a token source for the request
  if (cancelRef && (cancelRef?.current === undefined || cancelRef?.current === null)) {
    cancelRef.current = axios.CancelToken.source();
  }

  if (config.loader) {
    store.dispatch({ type: `setLoader`, payload: { key: config.loader, isLoading: true, loadStart: new Date() } });
  }

  let parsedStartData = config.data;
  if (typeof config.parseOptimisticUpdate === 'function') {
    const state = store.getState();
    parsedStartData = await config.parseOptimisticUpdate({ data: config.data, meta: config.meta, store: state });
    actionTypes.forEach((n) => {
      const payload = {
        success: true,
        ...parsedStartData,
        meta: {
          ...config.meta,
          ...parsedStartData.meta
        }
      };
      store.dispatch({ type: `${n}_SUCCESS`, payload });
    });
    if (callback) {
      callback(parsedStartData);
      callback = undefined;
    }
  }

  const dispatches = actionTypes.map((n) => dispatchHelper(n, store.dispatch));

  // A request that brings its own token (signup cleanup, before the session is active) must not wait on the ambient session.
  const bearerToken = config.headers?.Authorization ? null : await window?.Clerk?.session?.getToken();

  try {
    // eslint-disable-next-line new-cap
    const request = new axios.create({
      timeout: 1000 * 60 * 10,
      // Use text responseType to prevent the Axios fetch adapter from calling
      // Response.json(), which throws an unguarded SyntaxError when the server
      // (or an upstream proxy/CDN) returns a plain-text error body such as
      // "Rate exceeded." instead of JSON.
      responseType: 'text',
      transformResponse: [
        (data) => {
          if (typeof data === 'string') {
            try {
              return JSON.parse(data);
            } catch {
              console.warn('[apiRequest] Non-JSON response body received:', { url: config.url, bodyPreview: String(data).slice(0, 120) });
              return data;
            }
          }
          return data;
        }
      ]
    });

    request.interceptors.response.use((response) => {
      if (response.headers['x-custom-content-type'] === 'superjson') {
        const parsedSuperjsonResult = parseJson(response?.data?.superjson, { decodeJSTypes: true, fallback: false });
        const originalJson = response?.data?.json;

        response.data = parsedSuperjsonResult;

        // fallback to original json if needed.
        if (parsedSuperjsonResult === undefined) {
          response.data = originalJson;
        }
      }
      return response;
    });

    const companyId = getCompanyIdForRequest();
    const headers = {
      Authorization: `Bearer ${bearerToken}`,
      'x-mindcloud-instance': xMindcloudInstance,
      ...(companyId ? { 'X-Company-Id': companyId } : {}),
      ...(config || {}).headers
    };

    let response = await request({
      url: config.url,
      data: config.data,
      crossDomain: config.crossDomain,
      method: config.method,
      headers,
      cancelToken: cancelRef?.current?.token
    });

    if (config.onResponse) {
      response = config.onResponse(response);
    }

    dispatches.forEach((n) => n(response, callback, config.meta, serverCallback));

    if (config.loader) {
      store.dispatch({ type: `setLoader`, payload: { key: config.loader, isLoading: false, loadEnd: new Date() } });
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      return;
    }

    if (error.response?.status === 403 && error.response?.code === 'MISSING_PERMISSIONS') {
      sendToast({
        message: error.response.data.message,
        type: 'error'
      });
    }

    if (error.response?.status === 403 && error.response?.data?.code === ORG_ACCESS_RESTRICTED_CODE) {
      handleOrgAccessRestricted(error.response.data);
    }

    const err = error.response || error.request || error;
    dispatches.forEach((n) => n(err, callback, config.meta, serverCallback));
    if (config.loader) {
      store.dispatch({ type: `setLoader`, payload: { key: config.loader, isLoading: false, loadEnd: new Date() } });
    }
  }
};

const apiRequest = (method, url, config = {}) => {
  const query = { ...config.query };
  const params = String(mapObjectToQueryParams(query)).slice(1);
  const fixedUrl = String(String(url).match(/^https*:\/\//) ? url : `${BASE_API}${url}?${params}`);
  return new Promise(async (resolve) => {
    const callback = (nothing, data, err) => {
      resolve(data, err);
      if (config.callback && typeof config.callback === 'function') {
        config.callback(data, err);
      }
    };
    const serverCallback = (nothing, data, err) => {
      if (config.serverCallback && typeof config.serverCallback === 'function') {
        config.serverCallback(data, err);
      }
    };
    await nonBlockingWaitToBeLoaded();
    handleAPIRequest({
      url: fixedUrl,
      data: config.data,
      headers: config.headers,
      action: config.action,
      meta: config.meta,
      loader: config.loader,
      parseOptimisticUpdate: config.parseOptimisticUpdate,
      onResponse: config.onResponse || null,
      cancelRef: config.cancelRef,
      callback,
      serverCallback,
      method
    });
  });
};

export default apiRequest;
